import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import moment from 'moment';
import { v1 as uuidv1 } from 'uuid';
import {
  Amenity,
  FareTotalInPrefCurrency,
  FlightResult,
  Leg,
  TripType,
} from 'interfaces/flights';
import { LocationData } from 'interfaces/locations';
import { SearchQueryParams } from 'containers/SearchField';

export interface Coordinates {
  lat: number;
  lon: number;
}

export interface TransferLocations {
  [id: string]: Array<LocationData>;
}

export interface SelectedCardsFareTotal {
  outBound: FareTotalInPrefCurrency[] | null;
  return: FareTotalInPrefCurrency[] | null;
}

export interface PackageData {
  bookingId?: string;
  default_fare_family: string;
  default_fare_family_description: Amenity[];
  packagePrice: FareTotalInPrefCurrency[];
}

export interface SelectedPackages {
  outBound: PackageData | null;
  return: PackageData | null;
}

export interface OutboundDetails {
  outbound?: Leg;
  fromCountry?: string;
  toCountry?: string;
  fareTotal?: FareTotalInPrefCurrency[];
  date: string;
}

export interface IAirfareSearchState {
  r: string;
  radius250Km: boolean;
  extraDays: boolean;
  fromDestination: LocationData | null;
  toDestination: LocationData | null;
  fromDate: number;
  toDate: number | null;
  tripType: TripType;
  coupon: string | null;
  fromDestinationCoords: Array<number>;
  toDestinationCoords: Array<number>;
  flightTransferDetails: TransferLocations;
  selectedPackages: SelectedPackages;
  selectedCardsFareTotal: SelectedCardsFareTotal;
  loadedPackagesBookingId: Array<string>;
  searchFlights: FlightResult[] | null;
  outboundDetails: OutboundDetails | null;
  showSearchField: boolean;
}

const initialState: IAirfareSearchState = {
  r: uuidv1(),
  radius250Km: false,
  extraDays: false,
  tripType: 'roundtrip',
  coupon: null,
  fromDestination: null,
  toDestination: null,
  fromDate: moment().valueOf(),
  toDate: moment().add(7, 'day').valueOf(),
  fromDestinationCoords: [],
  toDestinationCoords: [],
  flightTransferDetails: {},
  selectedPackages: { outBound: null, return: null },
  selectedCardsFareTotal: { outBound: null, return: null },
  loadedPackagesBookingId: [],
  searchFlights: null,
  outboundDetails: null,
  showSearchField: false,
};

const airfareSearch = createSlice({
  name: 'airfareSearch',
  initialState,
  reducers: {
    set250KmRadius: (state, action: PayloadAction<void>) => {
      state.radius250Km = !state.radius250Km;
    },
    setExtraDays: (state, action: PayloadAction<void>) => {
      state.extraDays = !state.extraDays;
    },
    setOutboundDetails: (
      state,
      action: PayloadAction<OutboundDetails | null>,
    ) => {
      state.outboundDetails = action.payload;
    },
    setTripType: (state, action: PayloadAction<TripType>) => {
      const tt = action.payload;
      if (tt == state.tripType) return;
      if (tt == 'one-way') state.toDate = null;
      else {
        state.toDate = moment(state.fromDate).add(7, 'day').valueOf();
      }
      state.tripType = tt;
    },
    setCoupon: (state, action: PayloadAction<string | null>) => {
      state.coupon = action.payload;
    },
    setFromDestination: (state, action: PayloadAction<LocationData | null>) => {
      state.fromDestination = action.payload;
    },
    setShowSearchField: (state, action: PayloadAction<boolean>) => {
      state.showSearchField = action.payload;
    },
    setToDestination: (state, action: PayloadAction<LocationData | null>) => {
      state.toDestination = action.payload;
    },
    setLoadedPackagesBookingId: (
      state,
      action: PayloadAction<Array<string> | string>,
    ) => {
      if (Array.isArray(action.payload)) {
        state.loadedPackagesBookingId = action.payload;
      } else {
        state.loadedPackagesBookingId = [
          ...state.loadedPackagesBookingId,
          action.payload,
        ];
      }
    },
    setFromDestinationCoords: (state, action: PayloadAction<Array<number>>) => {
      state.fromDestinationCoords = action.payload;
    },
    setToDestinationCoords: (state, action: PayloadAction<Array<number>>) => {
      state.toDestinationCoords = action.payload;
    },
    setSelectedCardsFareTotal: (
      state,
      action: PayloadAction<{
        direction: 'outbound' | 'return';
        fareTotalInPrefCurr: FareTotalInPrefCurrency[] | null;
      }>,
    ) => {
      const { direction, fareTotalInPrefCurr } = action.payload;
      if (direction === 'outbound')
        state.selectedCardsFareTotal.outBound = fareTotalInPrefCurr;
      else state.selectedCardsFareTotal.return = fareTotalInPrefCurr;
    },
    setFlightTransferDetails: (
      state,
      action: PayloadAction<TransferLocations>,
    ) => {
      state.flightTransferDetails = action.payload;
    },
    setFromDate: (state, action: PayloadAction<number | null>) => {
      if (action.payload) state.fromDate = action.payload;
      if (state.toDate && state.fromDate > state.toDate)
        state.toDate = state.toDate;
    },
    setToDate: (state, action: PayloadAction<number | null>) => {
      state.tripType = action.payload === null ? 'one-way' : 'roundtrip';
      state.toDate = action.payload || null;
    },
    setInitial: (state, action: PayloadAction<SearchQueryParams>) => {
      const query = action.payload;
      if (query.r) state.r = query.r;
      // if (query.floc) setFromLocation(query.floc);
      // if (query.tloc) setToLocation(query.tloc);
      if (query.fromDate) state.fromDate = moment(query.fromDate).valueOf();
      if (query.toDate) state.toDate = moment(query.toDate).valueOf();
      if (query.extraDays)
        state.extraDays = parseInt('' + query.extraDays) === 3;
      if (query.tripType) state.tripType = query.tripType;
    },
    updateSessionId: (state, action: PayloadAction<string>) => {
      state.r = action.payload;
    },
  },
});

export const { actions } = airfareSearch;

export default airfareSearch.reducer;
